// ── Entity identity: where a debtor is, and whether we actually know ────────
//
// One reading of stateoforganization, shared by the form, the account page and
// (later) the UCC register, so the three cannot drift into slightly different
// opinions about whether a value is trustworthy.
//
// The distinction this file exists to keep visible:
//
//   Accounts.state                  where the company OPERATES (headquarters)
//   BorrowersGuarantors
//     .stateoforganization          where the entity is REGISTERED
//
// UCC 9-307(e) uses the second to decide which filing office is correct. They
// agree for most borrowers and disagree for exactly the ones that matter — a
// Delaware LLC run out of North Carolina is the common shape in this book, not
// an exotic one. A financing statement filed in the operating state perfects
// nothing, and nothing about it looks wrong afterwards.
//
// Every existing row was SEEDED from the headquarters state, so a populated
// value proves only that somebody's guess was written down. That is why
// "provisional" is a state of its own rather than being folded into "set".

export const STATE_OF_ORG_STATES = ["provisional", "confirmed", "missing", "na"];

export const stateOfOrgState = e => {
  const entity = e || {};
  const value = String(entity.stateoforganization || "").trim().toUpperCase();

  // An individual debtor has no state of organization at all — 9-307(b) locates
  // them at their principal residence. Reporting them as "missing" would put a
  // permanent item on a worklist that can never be actioned, which is how a
  // worklist stops being read.
  if (entity.entitytype === "Individual") {
    return { key: "na", label: "—", bg: null, fg: null,
             note: "An individual is located at their principal residence, not a state of organization." };
  }

  if (!value) {
    return { key: "missing", label: "Not set",
             bg: "#FBE4DD", fg: "#993C1D",
             note: "No state of organization recorded, so there is no way to tell which filing office is the right one." };
  }

  if (!entity.stateoforg_confirmedat) {
    return { key: "provisional", label: value + " · unverified",
             bg: "#FDF0D5", fg: "#7A5200",
             note: "Copied from the account's headquarters state as a starting point. Check it against the entity's "
                 + "charter and confirm — a company can operate in one state and be registered in another." };
  }

  return { key: "confirmed", label: value,
           bg: "#E1F5EE", fg: "#0F6E56",
           note: entity.stateoforg_confirmedby
             ? "Confirmed by " + entity.stateoforg_confirmedby
             : "Confirmed." };
};

// The one question the rest of the app actually asks: may this entity be named
// as a debtor on a filing we make ourselves? A guess is not good enough for
// that, even though it is fine to record against a filing the lawyers made.
export const canFileAgainst = e =>
  stateOfOrgState(e).key === "confirmed" || stateOfOrgState(e).key === "na";

export const StateOfOrgBadge = ({ entity }) => {
  const s = stateOfOrgState(entity);
  if (!s.bg) return <span style={{ color: "var(--color-text-secondary)" }}>{s.label}</span>;
  return (
    <span title={s.note || s.label}
          style={{ fontSize: 11.5, background: s.bg, color: s.fg, padding: "2px 8px",
                   borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
};
